'use client';

/**
 * Calendar subscribe / import buttons for the public `/events` toolbar.
 *
 * Renders "Add to Google Calendar" (Google subscribe-by-URL) plus an "iCal"
 * dropdown offering a live webcal subscription, a copy-feed-URL action, and a
 * one-time `.ics` download — all pointed at the `/api/events/calendar.ics`
 * feed. URLs are resolved from the browser origin at click time so the feed
 * works on any deployment host.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CalendarPlus, ChevronDown, Copy, Download, Rss } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

/** Path of the calendar feed relative to the site origin. */
const FEED_PATH = '/api/events/calendar.ics';

/**
 * Toolbar cluster of calendar-subscription actions. Client-only because it
 * reads `window.location` to build absolute feed URLs.
 */
export default function CalendarSubscribeButtons() {
  const { toast } = useToast();
  const [origin, setOrigin] = useState('');

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  const httpsUrl = `${origin}${FEED_PATH}`;
  const webcalUrl = origin
    ? `webcal://${origin.replace(/^https?:\/\//, '')}${FEED_PATH}`
    : '';
  // Google's "add other calendar by URL" deep link.
  const googleUrl = origin
    ? `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(httpsUrl)}`
    : '';

  /** Copy the subscription URL to the clipboard with toast feedback. */
  const copyFeedUrl = async () => {
    try {
      await navigator.clipboard.writeText(httpsUrl);
      toast({
        title: 'Calendar URL copied',
        description: 'Paste it into your calendar app to subscribe.',
      });
    } catch {
      toast({
        title: "Couldn't copy",
        description: httpsUrl,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button asChild variant="outline" className="gap-2">
        <a
          href={googleUrl || undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!origin}
        >
          <CalendarPlus className="w-4 h-4" />
          Add to Google Calendar
        </a>
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2" disabled={!origin}>
            <CalendarPlus className="w-4 h-4" />
            iCal
            <ChevronDown className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <a href={webcalUrl}>
              <Rss className="w-4 h-4" />
              Subscribe (Apple / Outlook)
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={copyFeedUrl}>
            <Copy className="w-4 h-4" />
            Copy feed URL
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href={`${FEED_PATH}?download=1`} download="srac-events.ics">
              <Download className="w-4 h-4" />
              Download .ics
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
